import React, { useEffect } from 'react';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { useRouting } from 'hooks/useRouting';
import { useModal } from 'hooks/useModal';
import { useCustomHeaderTitle } from 'hooks/useCustomHeaderTitle';
import { formatDate } from 'utils/formatter';
import { getPortfolioAnalysis, getTargetDetail } from 'config/api';
import { PremiumCheck2 as PremiumCheck } from 'components/commons/premium-check/PremiumCheck';
import AvatarIcon from 'components/features/utils/AvatarIcon';
import { commaFormat } from 'components/features/new-report/utils/formatter';
import { useCustomizeHeaderContext } from 'contexts/CustomizeHeaderContext';
import { useGlobalMenuContext } from 'contexts/GlobalMenuContext';
import { useTargetAchievementSupportContext } from 'contexts/TargetAchievementSupportContext';
import {
  ManagementItem,
  ManagementType,
  PortfolioAnalysis,
  PortfolioAnalysisItem,
  PortfolioAssetType,
  Target,
  TargetId,
  TargetModalStatus,
  TargetType,
} from '../utils/type';
import {
  getManagementItemInfo,
  portfolioTypeToBackgroundColor,
  portfolioTypeToString,
} from '../utils/portfolioInfo';
import { targetTypeToTitle } from '../utils/targetInfo';
import { useBirthday } from '../utils/birthday';
import { useTargetSupportRouting } from '../hooks/useTargetSupportRouting';
import { PortfolioAnalysisLoading } from '../components/features/portfolio-analysis/share/PortfolioAnalysisLoading';
import { TargetReturnInformation } from '../components/features/portfolio-analysis/index/TargetReturnInformation';
import { PortfolioChart } from '../components/features/portfolio-analysis/portfolio-chart';
import { PortfolioItemList } from '../components/features/portfolio-analysis/index/PortfolioItemList';
import { WhatIsInvestmetTypeModal } from '../components/features/portfolio-analysis/modal/WhatIsInvestmetTypeModal';
import { NotFoundBirthday } from '../components/features/birthday/NotFoundBirthday';
import { TargetPremium } from '../components/features/TargetPremium';
import { MonthlyTargetDecisionModal } from '../components/features/modal/MonthlyTargetDecisionModal';

const Hint = ({
  className,
}: {
  largestGapPortfolio: PortfolioAnalysisItem;
  className?: string;
}): React.ReactElement => {
  return (
    <>
      <div
        className={`border border-bluegray-300 rounded-lg bg-bluegray-100 p-4 flex flex-col ${className ??
          ''}`}>
        <div className="flex flex-row">
          <AvatarIcon icon="average" />
          <div className="flex flex-col ml-4">
            <div className="font-bold text-teal-500 text-xs">
              分散投資について
            </div>
            <div className="font-bold text-bluegray-700 text-sm mt-1">
              リスクが最小となる、投資商品の組み合わせの提案です
            </div>
          </div>
        </div>
        <div className="mt-2 text-bluegray-700 text-sm">
          長期投資では、リスクを分散し安定的に資産を運用することが大切です。
          <br />
          この提案では「投資商品の分散」することを目的に、ターゲットリターン（目標の利回り）以上が見込める、リスクが最小となる投資商品の組み合わせを提案しています。
        </div>
      </div>
    </>
  );
};

const NotFoundLargestGapPortfolio = (): React.ReactElement => {
  return (
    <div className="border rounded-lg border-bluegray-300 bg-bluegray-100 p-4">
      乖離の大きなポートフォリオが見つかりませんでした。
    </div>
  );
};

const calculateGap = (portfolio: PortfolioAnalysisItem): number => {
  return Math.abs(portfolio.currentPer - portfolio.targetPer);
};

const formatPortfolioUpdatedDate = (date: Date): string =>
  `${date.getFullYear()}/${(date.getMonth() + 1)
    .toString()
    .padStart(2, '0')}/${date
    .getDate()
    .toString()
    .padStart(2, '0')}(${
    ['日', '月', '火', '水', '木', '金', '土'][date.getDay()]
  }) ${date
    .getHours()
    .toString()
    .padStart(2, '0')}:${date
    .getMinutes()
    .toString()
    .padStart(2, '0')}`;

type Props = {
  target: Target;
  updatedDate: Date;
  portfolioAnalysis: PortfolioAnalysis;
};

const PortfolioAnalysisIndexInternal = ({
  target,
  portfolioAnalysis,
  updatedDate,
}: Props) => {
  const { setIsShowMenu } = useGlobalMenuContext();

  useEffect(() => {
    return setIsShowMenu(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const routing = useRouting();
  const backRouting = () => routing.backRouting(true, 1);
  const { setBack } = useCustomizeHeaderContext();
  const {
    goPortfolioAnalysisChangeAssets,
    goPortfolioAnalysisAssets,
    goTargetPushUpdateConfirm,
  } = useTargetSupportRouting();

  useEffect(() => {
    setBack({ backFunction: backRouting });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    updateGlobalTarget,
    updateConfirmGlobalTarget,
    updateIsFirstFlg,
  } = useTargetAchievementSupportContext();

  const [
    isShowMonthlyTargetDecisionModal,
    {
      hideModal: hideMonthlyTargetDecisionModal,
      showModal: showMonthlyTargetDecisionModal,
    },
  ] = useModal();

  const [
    isShowWhatIsInvestmetTypeModal,
    {
      hideModal: hideWhatIsInvestmetTypeModal,
      showModal: showWhatIsInvestmetTypeModal,
    },
  ] = useModal();

  // Google tag manager
  useEffect(() => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    if (userAgent.indexOf('chrome') !== -1) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'target_detail',
        targetinfo: `${target.id}_${target.title}_${targetTypeToTitle(
          target.refType
        )}_${formatDate(new Date(), 'yyyyMMddHHmmss')}`,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (target.status === 3) showMonthlyTargetDecisionModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (portfolioAnalysis === null || portfolioAnalysis === undefined)
    return <PortfolioAnalysisLoading />;

  const targetReturnLimitPercentage = portfolioAnalysis.targetReturnLimitPer;
  const targetReturnPercentage = portfolioAnalysis.targetReturnPer;
  const isTargetReturnLimitExcess =
    targetReturnPercentage > targetReturnLimitPercentage;

  const managementType = portfolioAnalysis.managementType;
  const managementItemInfo = getManagementItemInfo(managementType);
  if (managementItemInfo === undefined || managementType === undefined)
    return <></>;

  const managementItems = portfolioAnalysis.managementItems as ManagementItem[];
  const items = managementItems.find(
    managementItems => managementItems.managementType === managementType
  );
  if (items === undefined) return <></>;

  const portfolios = managementItemInfo.dispType.map(dispType => {
    const itemsByPortfolioAnalysis = items.itemsByPortfolioAnalysis as PortfolioAnalysisItem[];
    return itemsByPortfolioAnalysis.find(item => item.assetType === dispType);
  }) as PortfolioAnalysisItem[] | undefined;

  if (portfolios === undefined) return <></>;

  const largestGapPortfolio = portfolios.reduce((acc, v) => {
    if (!acc) return v;
    if (calculateGap(acc) >= calculateGap(v)) return acc;
    return v;
  }, null as PortfolioAnalysisItem | null);

  const sortPortfolios = portfolios.sort((a, b) =>
    a.targetPer - a.currentPer > b.targetPer - b.currentPer ? -1 : 1
  );

  const goUpdateTarget = () => {
    updateIsFirstFlg(true);
    updateGlobalTarget(null);
    updateConfirmGlobalTarget(null);
    goTargetPushUpdateConfirm(target.id, 'edit');
  };

  return (
    <>
      <div className="bg-white">
        <div className="flex flex-row pt-4 px-4">
          <div className="items-start">
            <AvatarIcon icon="average" />
          </div>
          <div className="flex flex-col text-bluegray-700 pl-4">
            <div className="font-bold text-sm">
              リスクをおさえて達成をめざす
            </div>
            <div className="font-bold text-base mt-1">
              ポートフォリオ（資産配分）の提案
            </div>
            <div className="font-normal text-sm mt-2">
              「目標」の資産比率に近づけることで、リスクを抑えて目標達成が目指せます。
            </div>
          </div>
        </div>

        <TargetReturnInformation
          {...{
            registeredAccounts: portfolioAnalysis.registeredAccountName,
            returnPercentage: targetReturnPercentage,
            isTargetReturnLimitExcess,
            targetReturnLimitPercentage,
            goUpdateTarget,
          }}
          className="mx-4 mt-6"
        />

        <div className="flex flex-row justify-end mt-1 text-bluegray-500 text-xs mx-8">
          資産情報の更新日時：
          {isTargetReturnLimitExcess
            ? '---'
            : formatPortfolioUpdatedDate(updatedDate)}
        </div>

        {isTargetReturnLimitExcess ? (
          <div className="flex justify-center mt-4 pb-6">
            <img
              className=""
              src={`/static/images/advisor/pie-chart-null.svg`}
              alt=""
            />
          </div>
        ) : (
          <>
            <div className="flex flex-row justify-evenly mx-4 mt-2">
              <PortfolioChart
                {...{
                  chart: {
                    title: '現在',
                    width: 135,
                    height: 135,
                    fontSize: '14px',
                  },
                  data: portfolios.map(p => {
                    return {
                      id: p.assetType,
                      value: p.currentPer,
                    };
                  }),
                  idToColor: portfolioTypeToBackgroundColor,
                  idToTitle: portfolioTypeToString,
                  valueFormatter: value =>
                    commaFormat(value, { numberOfDecimals: 0 }),
                }}
              />
              <div className="flex flex-row relative top-14 icon-arrow_right text-gray-400 text-4xl"></div>
              <div className="flex flex-col">
                <PortfolioChart
                  {...{
                    chart: {
                      title: '目標',
                      width: 135,
                      height: 135,
                      fontSize: '14px',
                    },
                    data: sortPortfolios.map(p => {
                      return {
                        id: p.assetType,
                        value: p.targetPer,
                      };
                    }),
                    idToColor: portfolioTypeToBackgroundColor,
                    idToTitle: portfolioTypeToString,
                    valueFormatter: value =>
                      commaFormat(value, { numberOfDecimals: 0 }),
                  }}
                />
                <div className="flex flex-col text-center">
                  <div className="text-1.5xs font-bold">
                    <span className={`${managementItemInfo.titleColor}`}>
                      {managementItemInfo.title}
                    </span>
                    {managementItemInfo.subTitle}
                  </div>
                  <div
                    className="flex flex-row items-center text-xs text-link mx-auto cursor-pointer"
                    onClick={() => goPortfolioAnalysisChangeAssets(target.id)}>
                    <div className="icon-arrow_right text-2xl"></div>
                    <div>運用タイプ変更</div>
                  </div>
                </div>
              </div>
            </div>

            <PortfolioItemList
              {...{
                portfolios: sortPortfolios,
                goToIssueDetail: (portfolio: PortfolioAnalysisItem) =>
                  goPortfolioAnalysisAssets(target.id, portfolio.assetType),
              }}
              className="mx-4 mt-6"
            />
            <div
              className="m-4 rounded-lg border"
              style={{
                borderColor: '#DEE5EE',
              }}>
              <div className="m-4 flex flex-col">
                <div className="flex flex-row items-center bg-teal-500 text-white py-1 px-2 mr-auto rounded-full">
                  <img
                    style={{ width: '20px', height: '20px' }}
                    src={`/static/images/advisor/asset_icon/icon_check.svg`}
                    alt="アイコン"
                  />
                  <div className="text-xs font-bold ml-2">現在の運用タイプ</div>
                </div>
                <div className="flex flex-row items-center mt-2 ml-2">
                  <img
                    style={{ width: '40px', height: '40px' }}
                    src={`/static/images/advisor/asset_icon/${managementItemInfo.imgName}.svg`}
                    alt="アイコン"
                  />
                  <div className="ml-4 text-base font-bold">
                    <span className={`${managementItemInfo.titleColor}`}>
                      {managementItemInfo.title}
                    </span>
                    <span
                      className={`${
                        managementItemInfo.type === 2 ||
                        managementItemInfo.type === 1
                          ? ''
                          : 'text-xs'
                      }`}>
                      {managementItemInfo.subTitle}
                    </span>
                  </div>
                </div>
                <div
                  className="mt-2 flex flex-row items-center justify-center border border-bluegray-400 rounded-lg py-2 px-4 bg-gray-50 text-sm font-bold text-bluegray-700 cursor-pointer"
                  onClick={() => goPortfolioAnalysisChangeAssets(target.id)}>
                  運用タイプ変更
                </div>
              </div>
            </div>
            <div className="flex justify-end mt-4 mx-4 text-bluegray-500 text-sm">
              <div
                className="flex items-center cursor-pointer"
                onClick={showWhatIsInvestmetTypeModal}>
                <div>現在の運用タイプとは</div>
                <div className="icon-help_outline ml-1"></div>
              </div>
            </div>
            <div className="p-4">
              {largestGapPortfolio ? (
                <Hint
                  {...{
                    largestGapPortfolio,
                  }}
                  className="mt-4"
                />
              ) : (
                <NotFoundLargestGapPortfolio />
              )}
              <div className="mt-4 text-xs text-bluegray-500">
                <div>【ご注意】</div>
                <div>
                  以下の資産は「その他」に分類されます。
                  <br />
                  ・マネーツリー連携しているすべての資産
                  <br />
                  ・現金、手入力資産
                  <br />
                  ・データ取得中の銘柄情報
                  <br />
                  ・一部のアクティブファンド等、分類ができない投資信託
                </div>
              </div>
              <div className="mt-4 text-xs text-bluegray-500">
                <div>【保有資産のポートフォリオ分類の方法について】</div>
                <div>
                  ファンドがベンチマークや参考指標としているインデックスをもとに分類しアセットクラスへマッピングしています。
                  <br />
                  分類が困難な投資信託、バランス型投資信託、コモデティティ関連に投資する投資信託、転換社債に投資する投資信託は「その他」として対象外とします。
                  <br />
                  先進国・新興国株式（広域）、先進国・新興国債券（広域）については、分析上はそれぞれエクスポージャーの高い先進国株式、先進国債券に分類しています。
                </div>
              </div>
              <div className="mt-4 text-xs text-bluegray-500">
                <div>【提案ポートフォリオ比率の算出方法】</div>
                <div>
                  アセットクラス毎に代表となるインデックスのヒストリカルデータを用いて期待リターン及びリスクを算出しています。ターゲットリターン毎にリスク最小となるような最適なアロケーションを提案します。
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <WhatIsInvestmetTypeModal
        {...{
          isShow: isShowWhatIsInvestmetTypeModal,
          hide: hideWhatIsInvestmetTypeModal,
        }}
      />

      {target.status === 3 && (
        <MonthlyTargetDecisionModal
          {...{
            isShow: isShowMonthlyTargetDecisionModal,
            hide: hideMonthlyTargetDecisionModal,
            id: target.id,
            title: target.title,
            type: target.refType,
            status: target.status,
          }}
        />
      )}
    </>
  );
};

const PortfolioAnalysisIndex = (): React.ReactElement => {
  const { id } = useParams<{ id: string }>();
  useCustomHeaderTitle('ポートフォリオ分析');

  const { data: target, isFetching: targetDetaiIsFetching } = useQuery(
    'portfolioAnalysisIndexTargetDetail',
    () => getTargetDetailAPI(id),
    {
      refetchOnWindowFocus: false,
    }
  );

  const {
    data: portfolioAnalysis,
    isFetching: portfolioAnalysisIsFetching,
  } = useQuery(
    'portfolioAnalysisIndexPortfolioAnalysis',
    () => getPortfolioAnalysisAPI(id),
    {
      refetchOnWindowFocus: false,
    }
  );

  const { birthday } = useBirthday();

  if (
    target === null ||
    target === undefined ||
    portfolioAnalysis === null ||
    portfolioAnalysis === undefined ||
    targetDetaiIsFetching ||
    portfolioAnalysisIsFetching
  )
    return <PortfolioAnalysisLoading />;

  if (birthday === null || birthday === undefined)
    return (
      <>
        <PremiumCheck content={<TargetPremium />}>
          <NotFoundBirthday />
        </PremiumCheck>
      </>
    );

  return (
    <PremiumCheck content={<TargetPremium />}>
      <PortfolioAnalysisIndexInternal
        {...{ target, portfolioAnalysis, updatedDate: new Date() }}
      />
    </PremiumCheck>
  );
};

const getTargetDetailAPI = async (id: string) => {
  const res_ = await getTargetDetail(id);
  const res = {
    id: res_.ID,
    title: res_.Title,
    refType: res_.RefType as TargetType,
    startDate: res_.StartDate,
    endDate: res_.EndDate,
    targetAmount: res_.TargetAmount,
    accountList: res_.AccountList.map(list => {
      return {
        account: list.Account,
        brokerName: list.BrokerName,
        available: list.Available,
        totalAssetAmount: list.TotalAssetAmount,
      };
    }),
    excludeAmount: res_.ExcludeAmount,
    reserveAmount: res_.ReserveAmount,
    assetAmount: res_.AssetAmount,
    yield: res_.Yield,
    achieved: res_.Achieved,
    status: res_.Status as TargetModalStatus,
  };

  return res as Target;
};

const getPortfolioAnalysisAPI = async (id: TargetId) => {
  const res_ = await getPortfolioAnalysis(id);
  const res = {
    itemsByPortfolioAnalysis: res_.ItemsByPortfolioAnalysis.map(item => {
      return {
        assetType: item.AssetType as PortfolioAssetType,
        currentPer: item.CurrentPer,
        targetPer: item.TargetPer,
      };
    }),
    registeredAccountName: res_.RegisteredAccountName,
    targetReturnPer: res_.TargetReturnPer,
    targetReturnLimitPer: res_.TargetReturnLimitPer,
    updateDateOfAsset: res_.UpdateDateOfAsset,
    managementType: res_.ManagementType as ManagementType,
    managementItems: res_.ManagementItems.map(managementItem => {
      return {
        managementType: managementItem.ManagementType as ManagementType,
        itemsByPortfolioAnalysis: managementItem.ItemsByPortfolioAnalysis.map(
          item => {
            return {
              assetType: item.AssetType as PortfolioAssetType,
              currentPer: item.CurrentPer,
              targetPer: item.TargetPer,
            };
          }
        ),
      };
    }),
  };

  return res as PortfolioAnalysis;
};

export default PortfolioAnalysisIndex;
